import React from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import { NavLink } from 'react-router-dom'
import ClickOutside from 'react-click-outside'
import classNames from 'classnames'

import { navSectionPropType } from '../common-proptypes'
import { navigationBarGa } from '../../../lib/analytics/ga'
import { customDimensions } from '../../../lib/analytics/custom-dimensions'
import NAV_ITEM_FRAGMENT from '../../../../graphql/fragments/nav-item.gql'

import styles from './navigation-side.css'
import { E2E_SELECTORS } from '../../../e2e-selectors'

const NavigationSideUi = ({
  display,
  isAuthenticated,
  mainSectionLinks,
  propertiesCount,
  profileId,
  onClickOutside,
  name,
  avatarUri,
  isKid,
  handleProfileChange,
  expandOnArrow,
  isAccountNavOpen,
  onToggleAccountNav,
  onSideNavLinkClick,
  myRentalsTotalItems,
  navigationItem: NavigationItem,
  navigationBurgerIcon: NavigationBurgerIcon,
  navigationSettings: NavigationSettings,
  navigationAccountSide: NavigationAccountSide
}) => {
  // TODO: Hardcoded Search nav item - replace with dynamic navigation data from API or config
  const searchItem = {
    name: 'Search',
    __typename: 'NavItem',
    path: {},
    target: 0,
    type: 'SCREEN_ID',
    children: [],
    isRental: false,
    isDiscoverNav: false
  }

  // Check if the user is authenticated and a kid for showing the Exit Kids button
  const renderExitKids = (isAuthenticated && isKid)

  return (
    <ClickOutside onClickOutside={onClickOutside}>
      <div
        className={classNames(styles.sideNavWrapper, {
          [styles.hidden]: !display
        })}
        data-lbx-e2e="nav nav-side"
      >
        <div className={styles.sideNav}>
          {/* Header Section */}
          <div
            className={classNames({
              [styles.header]: true,
              [styles.navLogged]: isAuthenticated
            })}
          >
            <div className={styles.logoWrapper}>
              <NavigationBurgerIcon display={display} />
              <NavLink
                exact
                to="/"
                onClick={() => {
                  navigationBarGa({
                    action: 'Logo',
                    [customDimensions.ProfileId]: profileId
                  })
                }}
                className={classNames(styles.navLink, styles.navLinkLogo)}
              />
            </div>
          </div>

          {/* Account Section under profile */}
          {isAuthenticated && (
            <NavigationAccountSide
              userName={name}
              isKid={isKid}
              avatarUri={avatarUri}
              isAccountNavOpen={isAccountNavOpen}
              myRentalsTotalItems={myRentalsTotalItems}
              onToggleAccountNav={onToggleAccountNav}
              onSwitchProfileClick={handleProfileChange}
              onSideNavLinkClick={onSideNavLinkClick}
            />
          )}

          {/* Main Navigation Section */}
          <div className={styles.mainSection}>
            {mainSectionLinks.map((navItem, idx) => {
              if (navItem.isDiscoverNav) {
                return null
              }

              return (
                <NavigationItem
                  inSideNav
                  key={idx} // eslint-disable-line react/no-array-index-key
                  navItem={navItem}
                  className={styles.navLink}
                  activeClassName={styles.navLinkActive}
                  wrapperClassName={isKid ? styles.kidsNavLinkWrapper : undefined}
                  propertiesCount={propertiesCount}
                  expandOnArrow={expandOnArrow}
                />
              )
            })}

            {/* Search item - only for adults and guest */}
            {!renderExitKids && (
              <NavigationItem
                inSideNav
                key="search"
                navItem={searchItem}
                className={styles.navLink}
              />
            )}
          </div>

          {/* Settings - only for adults */}
          {!isKid && <NavigationSettings inSideNav />}

          {/* Exit Kids button - only for kids profile */}
          {renderExitKids && (
            <div
              className={styles.exitKids}
              onClick={handleProfileChange}
              data-lbx-e2e={E2E_SELECTORS.EXIT_KIDS_NAVSIDE}
            >
              Exit Kids
            </div>
          )}
        </div>
      </div>
    </ClickOutside>
  )
}

NavigationSideUi.propTypes = {
  display: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  mainSectionLinks: PropTypes.oneOfType([
    navSectionPropType,
    PropTypes.arrayOf(propType(NAV_ITEM_FRAGMENT))
  ]).isRequired,
  propertiesCount: PropTypes.number.isRequired,
  profileId: PropTypes.string,
  onClickOutside: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  avatarUri: PropTypes.string.isRequired,
  isKid: PropTypes.bool.isRequired,
  handleProfileChange: PropTypes.func.isRequired,
  expandOnArrow: PropTypes.bool,
  isAccountNavOpen: PropTypes.bool,
  onToggleAccountNav: PropTypes.func,
  onSideNavLinkClick: PropTypes.func.isRequired,
  myRentalsTotalItems: PropTypes.number.isRequired,
  navigationItem: PropTypes.elementType.isRequired,
  navigationBurgerIcon: PropTypes.elementType.isRequired,
  navigationSettings: PropTypes.elementType,
  navigationAccountSide: PropTypes.elementType
}

NavigationSideUi.defaultProps = {
  profileId: null,
  expandOnArrow: true,
  isAccountNavOpen: false,
  onToggleAccountNav: () => {},
  navigationSettings: null,
  navigationAccountSide: null
}

export default NavigationSideUi
